import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { usePivot } from '../../context/PivotContext';
import { usePortalContainer } from '../../hooks/usePortalContainer';

/**
 * Drill-through popup. Opens when the user clicks a non-null value cell and
 * shows the subset of source rows that contributed to that aggregated value.
 *
 * The rows come from `matrix.sourceRows` (already filtered through the slice
 * filters), intersected on the clicked rowNode and colNode row indexes. Only
 * the fields actually present in the dataset metadata are rendered — no
 * synthetic date hierarchy columns (`.Year`, `.Month`, …) to keep the table
 * focused on the original record.
 */

const DrillThroughDialog = function DrillThroughDialog({
  open,
  onClose,
  title,
  rows,
  breadcrumbs,
}) {
  const { engine, localization: t, locale } = usePivot();
  const portalContainer = usePortalContainer();
  const [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    if (open) {
      setFilterText('');
      setSortBy(null);
      setSortDir('asc');
    }
  }, [open]);

  const columns = useMemo(() => {
    const meta = engine.getMetadata() || {};
    // Keep only the "physical" fields: exclude the expanded date-hierarchy
    // synthetic columns whose uniqueName contains a dot (".Year", ".Month" …).
    return Object.entries(meta)
      .filter(([uniqueName]) => !uniqueName.includes('.'))
      .map(([uniqueName, m]) => ({
        uniqueName,
        caption: m?.caption || uniqueName,
        type: m?.type,
      }));
  }, [engine, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatValue = (value, type) => {
    if (value === null || value === undefined || value === '') return '—';
    if (type === 'number' && Number.isFinite(Number(value))) {
      return new Intl.NumberFormat(locale || undefined).format(Number(value));
    }
    if (type === 'date' || type === 'time') {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString(locale || undefined);
      }
    }
    return String(value);
  };

  const handleSort = (uniqueName) => {
    if (sortBy === uniqueName) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(uniqueName);
      setSortDir('asc');
    }
  };

  const displayedRows = useMemo(() => {
    if (!rows) return [];
    const needle = filterText.trim().toLowerCase();
    let result = rows;
    if (needle) {
      result = rows.filter((row) =>
        columns.some((c) => {
          const formatted = formatValue(row?.[c.uniqueName], c.type);
          return formatted !== '—' && formatted.toLowerCase().includes(needle);
        })
      );
    }
    if (sortBy) {
      const col = columns.find((c) => c.uniqueName === sortBy);
      const dir = sortDir === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        const va = a?.[sortBy];
        const vb = b?.[sortBy];
        const aEmpty = va === null || va === undefined || va === '';
        const bEmpty = vb === null || vb === undefined || vb === '';
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        if (col?.type === 'number') {
          return (Number(va) - Number(vb)) * dir;
        }
        if (col?.type === 'date' || col?.type === 'time') {
          return (new Date(va).getTime() - new Date(vb).getTime()) * dir;
        }
        return String(va).localeCompare(String(vb), locale || undefined, { numeric: true }) * dir;
      });
    }
    return result;
  }, [rows, columns, filterText, sortBy, sortDir, locale]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="false"
      container={portalContainer}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {title || t?.drillThrough?.title || 'Detail data'}
        <Typography variant="caption" component="div" sx={{ opacity: 0.7 }}>
          {displayedRows.length}
          {filterText && rows?.length ? ` / ${rows.length}` : ''}{' '}
          {displayedRows.length === 1
            ? t?.drillThrough?.record || 'record'
            : t?.drillThrough?.recordsFound || 'records found'}
        </Typography>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Stack
            direction="row"
            sx={{
              gap: 0.5,
              flexWrap: "wrap",
              mt: 0.75
            }}>
            {breadcrumbs.map((b, idx) => (
              <Chip
                key={idx}
                size="small"
                label={
                  b.field ? (
                    <>
                      <Box component="span" sx={{ opacity: 0.6, mr: 0.5 }}>
                        {b.field}:
                      </Box>
                      <b>{b.value}</b>
                    </>
                  ) : (
                    <b>{b.value}</b>
                  )
                }
              />
            ))}
          </Stack>
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, padding: '16px' }}>
        {!rows || rows.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', opacity: 0.6 }}>
            <Typography variant="body2" sx={{
              fontStyle: "italic"
            }}>
              {t?.drillThrough?.noRecords || 'No records.'}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={t?.drillThrough?.filter || 'Filter...'}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell
                      key={c.uniqueName}
                      sortDirection={sortBy === c.uniqueName ? sortDir : false}
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        fontSize: 12,
                      }}
                    >
                      <TableSortLabel
                        active={sortBy === c.uniqueName}
                        direction={sortBy === c.uniqueName ? sortDir : 'asc'}
                        onClick={() => handleSort(c.uniqueName)}
                      >
                        {c.caption}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedRows.map((row, idx) => (
                  <TableRow key={idx} hover>
                    {columns.map((c) => (
                      <TableCell
                        key={c.uniqueName}
                        sx={{
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                          textAlign: c.type === 'number' ? 'right' : 'left',
                          fontVariantNumeric:
                            c.type === 'number' ? 'tabular-nums' : 'normal',
                        }}
                      >
                        {formatValue(row?.[c.uniqueName], c.type)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

DrillThroughDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  rows: PropTypes.array,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      value: PropTypes.string,
    })
  ),
};

export default DrillThroughDialog;
